import React from 'react';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { FloatingLabelInput } from '../components/shared/StyledComponents';
import { SingleValue } from 'react-select';
import styled from 'styled-components';
import { settingsService } from '../services/settingsService';
import api from '../services/api';
import { showError, showSuccess } from '../utils/sweetAlert';

// Opciones para el select de tipo de documento
const tipoDocumentoOptions = [
  { value: 'dni', label: 'DNI' },
  { value: 'ce', label: 'Carné de extranjería' },
  { value: 'pasaporte', label: 'Pasaporte' },
  { value: 'ptp', label: 'Permiso Temporal de Permanencia (PTP)' },
];

// Opciones para el select de distrito
const distritoOptions = [
  { value: 'calleria', label: 'Calleria' },
  { value: 'campoverde', label: 'CampoVerde' },
  { value: 'yarinacocha', label: 'Yarinacocha' },
  { value: 'manantay', label: 'Manantay' },
];

const customSelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    minHeight: '56px',
    borderRadius: '8px',
    borderColor: state.isFocused ? '#FDBF37' : '#ddd',
    boxShadow: state.isFocused ? '0 0 0 2px #FDBF3733' : 'none',
    '&:hover': { borderColor: '#FDBF37' },
    fontSize: '1.05rem',
    paddingTop: '0.5rem',
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    paddingTop: '1.2rem',
    paddingBottom: '0.2rem',
  }),
  placeholder: (provided: any, state: any) => ({
    ...provided,
    color: state.isFocused ? '#FDBF37' : '#aaa',
    fontSize: state.isFocused ? '0.92rem' : '1.05rem',
    position: 'absolute',
    top: state.hasValue || state.selectProps.menuIsOpen ? '-0.7rem' : '1.35rem',
    left: '1rem',
    background: '#fff',
    padding: '0 4px',
    transition: 'all 0.18s cubic-bezier(0.4,0,0.2,1)',
    zIndex: 3,
    pointerEvents: 'none',
  }),
  dropdownIndicator: (provided: any, state: any) => ({
    ...provided,
    color: state.isFocused ? '#FDBF37' : '#aaa',
    paddingTop: '1.2rem',
    paddingBottom: '0.2rem',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: (provided: any) => ({
    ...provided,
    borderRadius: 8,
    boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
    fontSize: '1.05rem',
    zIndex: 10,
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    color: '#222',
    background: state.isSelected ? '#FDBF37' : state.isFocused ? '#FFF3E0' : '#fff',
    fontWeight: state.isSelected ? 700 : 400,
    cursor: 'pointer',
    padding: '12px 18px',
  }),
};

const cardStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 320,
  background: '#fff',
  border: '1.5px solid #F3F3F3',
  borderRadius: 24,
  padding: '2.5rem 2rem',
  boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
  cursor: 'pointer',
  transition: 'box-shadow 0.2s, border-color 0.2s',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
};

const canalCardStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 320,
  background: '#fff',
  border: '1.5px solid #F3F3F3',
  borderRadius: 24,
  padding: '2.5rem 2rem',
  boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
  cursor: 'pointer',
  transition: 'box-shadow 0.2s, border-color 0.2s',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 16,
  marginBottom: 0,
};

// Styled-component para el label flotante del select
const FloatingLabel = styled.label<{ floating: boolean }>`
  position: absolute;
  left: 1rem;
  top: ${({ floating }) => (floating ? '-0.7rem' : '1.35rem')};
  font-size: ${({ floating }) => (floating ? '0.92rem' : '1.05rem')};
  color: ${({ floating }) => (floating ? '#FDBF37' : '#aaa')};
  background: #fff;
  padding: 0 4px;
  pointer-events: none;
  transition: all 0.18s cubic-bezier(0.4,0,0.2,1);
  z-index: 3;
  line-height: 1.1;
`;
const SelectWrapper = styled.div`
  position: relative;
  flex: 1;
  margin-right: 16px;
`;

const LibroReclamaciones: React.FC = () => {
  const navigate = useNavigate();
  // Fecha actual en formato DD/MM/AA
  const fecha = new Date();
  const fechaActual = `${fecha.getDate().toString().padStart(2, '0')}/${(fecha.getMonth()+1).toString().padStart(2, '0')}/${fecha.getFullYear().toString().slice(-2)}`;

  // Ampliar el estado de pasos para incluir 'producto'
  const [step, setStep] = React.useState<'tipo' | 'canal' | 'formulario' | 'producto'>('tipo');
  const [hovered, setHovered] = React.useState<'servicio' | 'producto' | null>(null);
  const [canalHovered, setCanalHovered] = React.useState<'fisica' | 'online' | null>(null);

  // Estados controlados para el formulario
  const [tipoDocumento, setTipoDocumento] = React.useState<{ value: string; label: string } | null>(null);
  const [numeroDocumento, setNumeroDocumento] = React.useState('');
  const [nombres, setNombres] = React.useState('');
  const [apellidos, setApellidos] = React.useState('');
  const [correo, setCorreo] = React.useState('');
  const [telefono, setTelefono] = React.useState('');
  const [departamento, setDepartamento] = React.useState('');
  const [provincia, setProvincia] = React.useState('');
  const [distrito, setDistrito] = React.useState('');
  const [direccion, setDireccion] = React.useState('');
  const [referencia, setReferencia] = React.useState('');
  const [menorEdad, setMenorEdad] = React.useState(false);
  const [envio, setEnvio] = React.useState('correo');

  // Estado para los campos del producto
  const [numeroPedido, setNumeroPedido] = React.useState('');
  const [descripcionProducto, setDescripcionProducto] = React.useState('');
  const [montoReclamado, setMontoReclamado] = React.useState('');

  // Estado para el label flotante del select
  const [selectFocused, setSelectFocused] = React.useState(false);

  // Manejar el submit de datos personales para avanzar al paso de producto
  const handleSubmitDatosPersonales = (e: React.FormEvent) => {
    e.preventDefault();
    // Aquí puedes agregar validaciones si lo deseas
    setStep('producto');
  };

  // Formulario de datos del producto (nuevo paso)
  const handleSubmitProducto = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // 1. Enviar por correo electrónico al backend
      const response = await api.post('/complaints', {
        tipoReclamo: step === 'tipo' ? (hovered || 'producto') : 'producto',
        canal: canalHovered || 'online',
        datosPersonales: {
          tipoDocumento: tipoDocumento?.label,
          numeroDocumento,
          nombres,
          apellidos,
          correo,
          telefono,
          distrito: distritoOptions.find(opt => opt.value === distrito)?.label,
          direccion,
          referencia,
          menorEdad,
          envio
        },
        datosProducto: {
          numeroPedido,
          descripcionProducto,
          montoReclamado
        },
        fecha: fechaActual
      });

      const { complaintNumber } = response.data;

      await showSuccess(`Tu reclamo ha sido enviado correctamente. El número de reclamo generado es: ${complaintNumber}. Nos pondremos en contacto contigo pronto.`);
      
      // Redirigir al inicio después de que el usuario cierre el mensaje de éxito
      navigate('/');
      
    } catch (error: any) {
      console.error('Error al enviar reclamo:', error);
      showError(error.response?.data?.message || 'Hubo un error al enviar el reclamo por correo.');
    }
  };

  // Paso 1: Selección de tipo de reclamo
  const renderTipo = () => (
    <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
      <div
        style={{
          ...cardStyle,
          border: hovered === 'servicio' ? '2.5px solid #FDBF37' : cardStyle.border,
        }}
        onMouseEnter={() => setHovered('servicio')}
        onMouseLeave={() => setHovered(null)}
        onClick={() => setStep('canal')}
      >
        <span style={{ color: '#FDBF37', fontSize: 40, marginBottom: 16 }}>
          <svg width="40" height="40" fill="none" viewBox="0 0 24 24"><path fill="#FDBF37" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4Zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4Z"/></svg>
        </span>
        <div style={{ fontWeight: 500, fontSize: 22, marginBottom: 8, transition: 'all 0.18s', position: 'relative', top: hovered === 'servicio' ? '-18px' : '0', color: hovered === 'servicio' ? '#FDBF37' : '#222' }}>
          Tuve un inconveniente<br />relacionado a un <b>servicio</b>
        </div>
        <div style={{ color: '#666', fontSize: 16 }}>
          Si tu reclamo está relacionado a una experiencia con nuestro proceso de compra, entrega o atención al cliente, elige esta opción.
        </div>
        <span style={{ marginLeft: 'auto', color: '#FDBF37', fontSize: 28, marginTop: 16 }}>&#8250;</span>
      </div>
      <div
        style={{
          ...cardStyle,
          border: hovered === 'producto' ? '2.5px solid #FDBF37' : cardStyle.border,
        }}
        onMouseEnter={() => setHovered('producto')}
        onMouseLeave={() => setHovered(null)}
        onClick={() => setStep('canal')}
      >
        <span style={{ color: '#FDBF37', fontSize: 40, marginBottom: 16 }}>
          <svg width="40" height="40" fill="none" viewBox="0 0 24 24"><path fill="#FDBF37" d="M7 18c-1.1 0-2-.9-2-2V8c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2v8c0 1.1-.9 2-2 2H7Zm0 2h10c2.21 0 4-1.79 4-4V8c0-2.21-1.79-4-4-4H7C4.79 4 3 5.79 3 8v8c0 2.21 1.79 4 4 4Z"/></svg>
        </span>
        <div style={{ fontWeight: 500, fontSize: 22, marginBottom: 8, transition: 'all 0.18s', position: 'relative', top: hovered === 'producto' ? '-18px' : '0', color: hovered === 'producto' ? '#FDBF37' : '#222' }}>
          Tuve un inconveniente<br />relacionado a un <b>producto</b>
        </div>
        <div style={{ color: '#666', fontSize: 16 }}>
          Si tu reclamo está relacionado a un producto, servicio de armado o instalación que adquiriste en cualquiera de nuestras tiendas físicas o en nuestra tienda online, elige esta opción.
        </div>
        <span style={{ marginLeft: 'auto', color: '#FDBF37', fontSize: 28, marginTop: 16 }}>&#8250;</span>
      </div>
    </div>
  );

  // Paso 2: Selección de canal
  const renderCanal = () => (
    <>
      <div style={{ marginBottom: 10 }}>
        <a href="#" style={{ color: '#FDBF37', fontWeight: 600, textDecoration: 'underline', fontSize: 16 }} onClick={e => { e.preventDefault(); setStep('tipo'); }}>
          &#60; Ir al paso anterior
        </a>
      </div>
      <h2 style={{ fontWeight: 700, fontSize: '1.7rem', marginBottom: 32, marginLeft: 8 }}>
        ¿<span style={{ fontWeight: 900 }}>A través de qué canal</span> realizaste la compra de tu producto?
      </h2>
      <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', marginBottom: 32 }}>
        <div
          style={{
            ...canalCardStyle,
            border: canalHovered === 'fisica' ? '2.5px solid #FDBF37' : canalCardStyle.border,
          }}
          onMouseEnter={() => setCanalHovered('fisica')}
          onMouseLeave={() => setCanalHovered(null)}
          onClick={() => setStep('formulario')}
        >
          <span style={{ color: '#FDBF37', fontSize: 40, marginBottom: 16 }}>
            {/* Icono tienda física */}
            <svg width="40" height="40" fill="none" viewBox="0 0 24 24"><path fill="#FDBF37" d="M4 7V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v1h1a1 1 0 0 1 1 1v1.38a3.62 3.62 0 0 1-7.24.62h-3.52A3.62 3.62 0 0 1 3 9.38V8a1 1 0 0 1 1-1h1Zm0 2.38A1.62 1.62 0 0 0 5.62 11h12.76A1.62 1.62 0 0 0 20 9.38V8H4v1.38ZM6 13v5a1 1 0 0 0 1 1h2v-4h6v4h2a1 1 0 0 0 1-1v-5H6Z"/></svg>
          </span>
          <div style={{ fontWeight: 400, fontSize: 22, marginBottom: 8, transition: 'all 0.18s', position: 'relative', top: canalHovered === 'fisica' ? '-18px' : '0', color: canalHovered === 'fisica' ? '#FDBF37' : '#222' }}>
            Tienda física
          </div>
          <span style={{ marginLeft: 'auto', color: '#FDBF37', fontSize: 28, marginTop: 16 }}>&#8250;</span>
        </div>
        <div
          style={{
            ...canalCardStyle,
            border: canalHovered === 'online' ? '2.5px solid #FDBF37' : canalCardStyle.border,
          }}
          onMouseEnter={() => setCanalHovered('online')}
          onMouseLeave={() => setCanalHovered(null)}
          onClick={() => setStep('formulario')}
        >
          <span style={{ color: '#FDBF37', fontSize: 40, marginBottom: 16 }}>
            {/* Icono tienda online */}
            <svg width="40" height="40" fill="none" viewBox="0 0 24 24"><path fill="#FDBF37" d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6Zm2 0v12h12V6H6Zm2 2h8v2H8V8Zm0 4h8v2H8v-2Z"/></svg>
          </span>
          <div style={{ fontWeight: 400, fontSize: 22, marginBottom: 8, transition: 'all 0.18s', position: 'relative', top: canalHovered === 'online' ? '-18px' : '0', color: canalHovered === 'online' ? '#FDBF37' : '#222' }}>
            Tienda online
          </div>
          <span style={{ marginLeft: 'auto', color: '#FDBF37', fontSize: 28, marginTop: 16 }}>&#8250;</span>
        </div>
      </div>
      <a href="#" style={{ color: '#888', fontWeight: 400, textDecoration: 'underline', fontSize: 16, marginLeft: 8 }} onClick={e => { e.preventDefault(); setStep('formulario'); }}>
        Omitir este paso <span style={{ color: '#FDBF37', fontSize: 22, marginLeft: 4 }}>&#8250;</span>
      </a>
    </>
  );

  // Paso 3: Formulario de datos personales
  function renderFormulario() {
    return (
      <>
        <div style={{ marginBottom: 10 }}>
          <a href="#" style={{ color: '#FDBF37', fontWeight: 600, textDecoration: 'underline', fontSize: 16 }} onClick={e => { e.preventDefault(); setStep('canal'); }}>
            &#60; Ir al paso anterior
          </a>
        </div>
        <h2 style={{ fontWeight: 700, fontSize: '2.1rem', marginBottom: 8, marginLeft: 8 }}>
          Ingresa tus <span style={{ fontWeight: 900 }}>datos personales</span>
        </h2>
        <p style={{ color: '#666', marginBottom: 24, fontSize: 17, marginLeft: 8 }}>
          Para poder ayudarte, ingresa la información solicitada.
        </p>
        <form style={{ marginLeft: 8, maxWidth: 1200 }} onSubmit={handleSubmitDatosPersonales}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 16, marginRight: 12 }}>
              Soy menor de edad <input type="checkbox" style={{ marginLeft: 6 }} checked={menorEdad} onChange={e => setMenorEdad(e.target.checked)} />
            </label>
          </div>
          <div style={rowStyle}>
            <SelectWrapper>
              <Select
                options={tipoDocumentoOptions}
                value={tipoDocumento}
                onChange={(option: SingleValue<{ value: string; label: string }>) => setTipoDocumento(option)}
                placeholder=""
                styles={customSelectStyles}
                isSearchable={false}
                menuPlacement="auto"
                onFocus={() => setSelectFocused(true)}
                onBlur={() => setSelectFocused(false)}
              />
              <FloatingLabel floating={selectFocused || !!tipoDocumento}>
                Tipo de documento
              </FloatingLabel>
            </SelectWrapper>
            <FloatingLabelInput label="N° de documento de identidad" style={{ flex: 1 }} value={numeroDocumento} onChange={e => setNumeroDocumento(e.target.value)} />
          </div>
          <div style={rowStyle}>
            <FloatingLabelInput label="Nombres" style={{ flex: 1 }} value={nombres} onChange={e => setNombres(e.target.value)} />
            <FloatingLabelInput label="Apellidos" style={{ flex: 1 }} value={apellidos} onChange={e => setApellidos(e.target.value)} />
          </div>
          <div style={rowStyle}>
            <FloatingLabelInput label="Correo electrónico" style={{ flex: 1 }} value={correo} onChange={e => setCorreo(e.target.value)} />
            <FloatingLabelInput label="Teléfono" style={{ flex: 1 }} value={telefono} onChange={e => setTelefono(e.target.value)} />
          </div>
          <div style={rowStyle}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', marginRight: 16 }}>
              <label style={{ position: 'absolute', left: 16, top: -10, fontSize: 15, color: '#aaa', background: '#fff', padding: '0 4px', zIndex: 2 }}>
                Departamento
              </label>
              <div style={{ padding: '18px 12px 6px 12px', border: '1px solid #ddd', borderRadius: 8, background: '#f5f5f5', color: '#888', fontSize: 16, minHeight: 40 }}>
                Ucayali
              </div>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', marginRight: 16 }}>
              <label style={{ position: 'absolute', left: 16, top: -10, fontSize: 15, color: '#aaa', background: '#fff', padding: '0 4px', zIndex: 2 }}>
                Provincia
              </label>
              <div style={{ padding: '18px 12px 6px 12px', border: '1px solid #ddd', borderRadius: 8, background: '#f5f5f5', color: '#888', fontSize: 16, minHeight: 40 }}>
                Coronel Portillo
              </div>
            </div>
            <SelectWrapper>
              <Select
                options={distritoOptions}
                value={distritoOptions.find(option => option.value === distrito) || null}
                onChange={(option: SingleValue<{ value: string; label: string }>) => setDistrito(option ? option.value : '')}
                placeholder=""
                styles={customSelectStyles}
                isSearchable={false}
                menuPlacement="auto"
              />
              <FloatingLabel floating={!!distrito}>
                Distrito
              </FloatingLabel>
            </SelectWrapper>
          </div>
          <div style={{ ...rowStyle, marginTop: 24 }}>
            <FloatingLabelInput label="Dirección" style={{ flex: 1 }} value={direccion} onChange={e => setDireccion(e.target.value)} />
            <FloatingLabelInput label="Referencia" style={{ flex: 1 }} value={referencia} onChange={e => setReferencia(e.target.value)} />
          </div>
          <div style={{ margin: '18px 0 24px 0', fontSize: 16 }}>
            ¿A dónde quieres que enviemos tu carta de respuesta?
            <div style={{ marginTop: 8 }}>
              <label style={{ marginRight: 18, color: '#FDBF37', fontWeight: 500 }}>
                <input type="radio" name="envio" value="correo" checked={envio === 'correo'} onChange={() => setEnvio('correo')} style={{ marginRight: 6 }} /> A mi correo electrónico
              </label>
              <label>
                <input type="radio" name="envio" value="domicilio" checked={envio === 'domicilio'} onChange={() => setEnvio('domicilio')} style={{ marginRight: 6 }} /> A mi domicilio
              </label>
            </div>
          </div>
          <button type="submit" style={{ background: '#FDBF37', color: '#fff', border: 'none', borderRadius: 22, padding: '0.9rem 2.5rem', fontSize: 20, fontWeight: 700, cursor: 'pointer', marginTop: 8 }}>
            Continuar
          </button>
        </form>
      </>
    );
  }

  function renderProducto() {
    return (
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '2rem 0' }}>
        <div style={{ marginBottom: 18 }}>
          <a href="#" style={{ color: '#FDBF37', fontWeight: 600, textDecoration: 'underline', fontSize: 16 }} onClick={e => { e.preventDefault(); setStep('formulario'); }}>
            &#60; Ir al paso anterior
          </a>
        </div>
        <h2 style={{ fontWeight: 700, fontSize: '2.1rem', marginBottom: 8, marginLeft: 2 }}>
          Ingresa los <span style={{ fontWeight: 900 }}>datos del producto</span>
        </h2>
        <p style={{ color: '#222', marginBottom: 32, fontSize: 16, marginLeft: 2 }}>
          Estos datos nos ayudarán a resolver tu reclamo de manera más rápida. Identifica tu pedido mucho más rápido <span style={{ color: '#FDBF37', textDecoration: 'underline', cursor: 'pointer' }}>iniciando sesión</span>. También puedes omitir este paso.
        </p>
        <form style={{ marginLeft: 2, maxWidth: 900 }} onSubmit={handleSubmitProducto}>
          <div style={{ display: 'flex', gap: 18, marginBottom: 24 }}>
            <FloatingLabelInput label="Número de pedido" style={{ flex: 1, position: 'relative' }} value={numeroPedido} onChange={e => setNumeroPedido(e.target.value)} />
            <div style={{ flex: 1, position: 'relative' }}>
              <FloatingLabelInput label="Descripción del producto" value={descripcionProducto} onChange={e => setDescripcionProducto(e.target.value)} />
              <span style={{ position: 'absolute', left: 14, bottom: -22, color: '#ccc', fontSize: 13 }}>
                Ej: Televisor HYUNDAI LED 32" HD Smart
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, marginBottom: 32 }}>
            <FloatingLabelInput label="Monto reclamado en soles" style={{ flex: 1 }} value={montoReclamado} onChange={e => setMontoReclamado(e.target.value)} />
          </div>
          <button type="submit" style={{ background: '#FDBF37', color: '#fff', border: 'none', borderRadius: 22, padding: '0.9rem 2.5rem', fontSize: 20, fontWeight: 700, cursor: 'pointer', marginTop: 8, width: 340, maxWidth: '100%', display: 'block', marginLeft: 'auto', marginRight: 'auto' }}>
            Continuar
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', minHeight: '80vh', padding: '2rem 0' }}>
      <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', gap: 0 }}>
        {/* Contenido principal a la izquierda */}
        <div style={{ flex: 1, marginLeft: 32, marginRight: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
            <h1 style={{ fontWeight: 600, fontSize: '2rem', marginBottom: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Cuéntanos sobre tu inconveniente
            </h1>
            <div style={{ background: '#F3F3F3', color: '#888', borderRadius: 8, padding: '0.5rem 1.6rem', fontWeight: 500, fontSize: 16, marginLeft: 32, marginTop: 8, minWidth: 140, textAlign: 'center', whiteSpace: 'nowrap' }}>
              FECHA: {fechaActual}
            </div>
          </div>
          <p style={{ color: '#666', marginBottom: 32, fontSize: 16, marginLeft: 2 }}>
            Ayúdanos completando los siguientes datos del Libro de Reclamaciones
          </p>
          {step === 'tipo' && renderTipo()}
          {step === 'canal' && renderCanal()}
          {step === 'formulario' && renderFormulario()}
          {step === 'producto' && renderProducto()}
        </div>
        {/* Recuadro informativo a la derecha */}
        <div style={{ display: 'inline-block', maxWidth: 360, background: '#F8F8F8', borderRadius: 16, padding: '2.5rem 2.2rem 1.2rem 2.8rem', color: '#444', fontSize: 15, lineHeight: 1.7, marginLeft: 48 }}>
          <b style={{ display: 'block', marginBottom: 8 }}>Libro de Reclamaciones del Código de Protección y Defensa del Consumidor</b>
          ponemos a tu disposición nuestro Libro de Reclamaciones Virtual.<br /><br />
          A través de nuestro Libro de Reclamaciones Virtual podrás presentar un reclamo o queja respecto a tu disconformidad por un producto o servicio ofrecido por HOME CENTERS PERUANOS S.A.<br /><br />
          Te informamos que los datos que ingreses al momento de presentar tu reclamo o queja, a través de nuestro Libro de Reclamaciones Virtual, serán tratados conforme nuestra <a href="#" style={{ color: '#FDBF37', textDecoration: 'underline' }}>Políticas de Datos Personales</a>, con la finalidad de gestionar y dar respuesta al mismo, dentro del plazo legal.
        </div>
      </div>
    </div>
  );
};

export default LibroReclamaciones;